import { memo, useEffect, useState } from 'react'
import { TERMS_VERSION } from '@lunya/contracts'

import { copy } from '~/modules/lunya'
import { PRIVACY_POLICY_URL, TERMS_URL } from '~/shared/config'
import { ANALYTICS_EVENTS, useAnalytics } from '~/shared/lib/analytics'
import { usePlatform } from '~/shared/platform'
import { BottomSheet, Button, Text, TextLink, VStack } from '~/shared/ui'

import { AcceptTermsMutation, UserQuery } from '../../api'
import type { TermsAcceptSheetProps } from './types'

/**
 * Оферта для тех, кто зарегистрировался раньше, чем она появилась.
 *
 * Задним числом акцепт не проставлен никому, и это принципиально: отметка о принятии документа,
 * которого человек не видел, — не доказательство, а недостоверные сведения оператора о самом
 * себе (миграция `20260818130000_terms_acceptances` говорит об этом же). Значит, спросить надо
 * по-настоящему, и спросить один раз.
 *
 * УСТРОЙСТВО. Виджет висит на корне приложения рядом с ловцом приглашений, а не на экране:
 * зарегистрированный человек открывает приложение на любом маршруте, и привязка к главной
 * означала бы, что часть людей документ не увидит вовсе. Условие показа одно — последняя
 * принятая редакция не совпала с действующей (`TERMS_VERSION`). Оно же само собой отработает
 * при выпуске следующей редакции: поднятая константа снова разойдётся с сохранённой.
 *
 * ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО:
 *
 *   • ГАЛОЧКИ. Кнопкой принимаются только условия; согласие на обработку ПД с 01.09.2025
 *     обязано быть отдельным документом, и одна галочка на всё сделала бы его неполученным
 *     (docs/legal.md §4.3).
 *   • КНОПКИ «ОТКАЗАТЬСЯ». Отказ от условий — это уход из сервиса, и оформлять его кнопкой
 *     в шторке значило бы предлагать удалить аккаунт человеку, который просто читает.
 *     Кто не готов — закрывает шторку свайпом; ничего не записывается, приложение работает,
 *     шторка вернётся при следующем запуске. Продукт про близость не имеет права держать
 *     человека в заложниках у документа (антибриф: никакого давления).
 *   • ПОВТОРНОГО ПОКАЗА В ТОМ ЖЕ ЗАПУСКЕ. Закрыли — значит закрыли; `isClosed` живёт до
 *     перезапуска мини-аппы.
 */
export const TermsAcceptSheet = memo(function TermsAcceptSheet(props: TermsAcceptSheetProps) {
  const { isEnabled } = props

  const platform = usePlatform()
  const { track } = useAnalytics()

  const { data: user } = UserQuery.useQuery(undefined, { enabled: isEnabled })
  const [acceptTerms, { isPending }] = AcceptTermsMutation.useMutation()
  const [isClosed, setIsClosed] = useState(false)

  // `null` (не принимал ни одной редакции) и «принял прошлую» — один и тот же случай:
  // действующие условия не приняты.
  const needsAcceptance = isEnabled && user !== undefined && user.termsVersion !== TERMS_VERSION
  const isOpen = needsAcceptance && !isClosed

  useEffect(() => {
    if (isOpen) track(ANALYTICS_EVENTS.termsSheetShown())
  }, [isOpen, track])

  if (!isOpen) return null

  const handleAccept = async () => {
    track(ANALYTICS_EVENTS.termsAccepted({ source: 'resume' }))
    // Шторка закрывается сразу, не дожидаясь ответа: человек своё действие совершил, и держать
    // его под затемнением на время сетевого запроса не за что. Запрос идемпотентен, а если он
    // не доедет — `termsVersion` останется прежним и шторка честно вернётся в следующий запуск.
    setIsClosed(true)
    await acceptTerms({ source: 'resume' })
  }

  return (
    <BottomSheet isOpen onClose={() => setIsClosed(true)}>
      <BottomSheet.Header>
        <VStack gap='SMALL_M' align='CENTER'>
          <Text variant='H1' as='h2' align='CENTER'>
            {copy.legal.resume.title}
          </Text>
          <Text variant='BODY_S' color='SECONDARY' align='CENTER'>
            {copy.legal.resume.body}
          </Text>
        </VStack>
      </BottomSheet.Header>
      <BottomSheet.Content>
        {/* Ссылки — отдельными строками и полными названиями документов: в шторке, где
            нажимают «Принимаю», документ обязан быть в один тап, а не спрятан в предложении. */}
        <VStack gap='SMALL_M' align='CENTER'>
          <TextLink
            text={copy.legal.terms_doc}
            variant='BODY_S'
            onPress={() => platform.share.openLink(TERMS_URL)}
          />
          <TextLink
            text={copy.legal.policy_doc}
            variant='BODY_S'
            onPress={() => platform.share.openLink(PRIVACY_POLICY_URL)}
          />
        </VStack>
      </BottomSheet.Content>
      <BottomSheet.Footer>
        <Button
          variant='PRIMARY'
          width='FULL'
          text={copy.legal.resume.cta}
          isLoading={isPending}
          onPress={() => void handleAccept()}
        />
      </BottomSheet.Footer>
    </BottomSheet>
  )
})
